import { useCallback, useRef } from 'react';

import { REMOVE_ANNOTATIONS, UPDATE_STATE } from '../actions';
import { TOOLS_IDS, WATERMARK_ANNOTATION_ID } from '../utils/constants';
import { filterUnlockedAnnotationIds } from '../utils/annotationLockUtils';
import randomId from '../utils/randomId';
import useStore from './useStore';

const PASTE_OFFSET_STEP = 20;

const cloneAnnotationForClipboard = (annotation) => {
  const { groupId, ...rest } = annotation;
  return { ...rest };
};

const useAnnotationClipboard = () => {
  const { dispatch, annotations = {}, selectionsIds = [] } = useStore();
  const clipboardRef = useRef(null);
  const pasteGenerationRef = useRef(0);

  const copySelection = useCallback(() => {
    const ids = filterUnlockedAnnotationIds(annotations, selectionsIds).filter(
      (id) => id !== WATERMARK_ANNOTATION_ID,
    );
    if (ids.length === 0) {
      clipboardRef.current = null;
      return false;
    }

    clipboardRef.current = {
      items: ids.map((id) => cloneAnnotationForClipboard(annotations[id])),
    };
    return true;
  }, [annotations, selectionsIds]);

  const hasClipboardData = useCallback(
    () => (clipboardRef.current?.items?.length ?? 0) > 0,
    [],
  );

  const pasteSelection = useCallback(() => {
    const items = clipboardRef.current?.items;
    if (!items?.length) {
      return false;
    }

    pasteGenerationRef.current += 1;
    const offset = PASTE_OFFSET_STEP * pasteGenerationRef.current;

    const newAnnotations = {};
    const newSelectionIds = [];

    items.forEach((item) => {
      const newId = randomId(item.name || TOOLS_IDS.RECT);
      newSelectionIds.push(newId);
      newAnnotations[newId] = {
        ...item,
        id: newId,
        x: (item.x ?? 0) + offset,
        y: (item.y ?? 0) + offset,
      };
    });

    dispatch({
      type: UPDATE_STATE,
      payload: {
        isDesignState: true,
        annotations: newAnnotations,
        selectionsIds: newSelectionIds,
      },
    });

    return true;
  }, [dispatch]);

  const cutSelection = useCallback(() => {
    const ids = filterUnlockedAnnotationIds(annotations, selectionsIds).filter(
      (id) => id !== WATERMARK_ANNOTATION_ID,
    );
    if (ids.length === 0) {
      return false;
    }

    if (!copySelection()) {
      return false;
    }

    dispatch({
      type: REMOVE_ANNOTATIONS,
      payload: {
        annotationsIds: ids,
        isDesignState: true,
      },
    });

    return true;
  }, [annotations, copySelection, dispatch, selectionsIds]);

  return {
    copySelection,
    pasteSelection,
    cutSelection,
    hasClipboardData,
  };
};

export default useAnnotationClipboard;
